import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
    { label: "Home", id: "hero" },
    { label: "Services", id: "features" },
    { label: "About Us", id: "about" },
    { label: "Work", id: "portfolio" },
    { label: "Blog", id: "blog" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header
            data-testid="main-navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "border-b border-[#00C6FF]/15 bg-[#050B14]/85 backdrop-blur-xl"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <button
                    data-testid="nav-logo"
                    onClick={() => go("hero")}
                    className="flex items-center"
                >
                    <img
                        src="/logo.webp"
                        alt="Prime 360 View — Explore. Experience. Grow."
                        data-testid="nav-logo-img"
                        className="logo-flip h-12 w-auto"
                    />
                </button>

                <nav className="hidden items-center gap-8 lg:flex">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => go(l.id)}
                            className="text-xs font-medium uppercase tracking-[0.2em] text-slate-300 transition-colors hover:text-[#00C6FF]"
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        data-testid="nav-quote-btn"
                        onClick={() => go("contact")}
                        className="rounded-full bg-[#00C6FF] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#050B14] transition-transform duration-300 hover:scale-105"
                    >
                        Get Quote
                    </button>
                </nav>

                <button
                    data-testid="nav-mobile-toggle"
                    className="text-white lg:hidden"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden border-t border-[#00C6FF]/10 bg-[#050B14]/95 backdrop-blur-xl lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-6 py-4">
                            {LINKS.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`nav-mobile-link-${l.id}`}
                                    onClick={() => go(l.id)}
                                    className="py-3 text-left text-sm uppercase tracking-[0.2em] text-slate-300"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                data-testid="nav-mobile-quote-btn"
                                onClick={() => go("contact")}
                                className="mt-2 rounded-full bg-[#00C6FF] px-5 py-3 text-xs font-bold uppercase tracking-[0.15em] text-[#050B14]"
                            >
                                Get Custom Quote
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
